import { api } from "./api";
import { config } from "./config";

// -------- Attempts (existing M6) --------
export interface AdminAttemptListItem {
  attemptId: string;
  candidateId: string;
  candidateName: string;
  email: string;
  mobile: string;
  candidateCategory: string;
  state: string;
  startedAt: string | null;
  submittedAt: string | null;
  questionsAttempted: number;
  questionsTotal: number;
  correct: number;
  wrong: number;
  skipped: number;
  positiveTotal: number;
  negativeTotal: number;
  penaltyTotal: number;
  finalScore: number;
  passFail: string | null;
  violationCount: number;
  snapshotCount: number;
  idVerificationStatus: string;
}

export interface AdminAttemptListResponse {
  quizId: string;
  quizTitle: string;
  items: AdminAttemptListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface AdminReviewOption { id: string; text: string; isCorrect: boolean; }
export interface AdminQuestionReview {
  attemptQuestionId: string;
  orderIndex: number;
  questionText: string;
  categoryName: string;
  difficulty: string;
  positiveMarksApplied: number;
  negativeMarksApplied: number;
  options: AdminReviewOption[];
  correctOptionId: string | null;
  selectedOptionId: string | null;
  isCorrect: boolean | null;
  answeredAt: string | null;
}
export interface AdminViolation { id: string; type: string; occurredAt: string; penaltyApplied: number; metadataJson: string; }
export interface AdminSnapshot { id: string; capturedAt: string; widthPx: number; heightPx: number; signedUrl: string; }
export interface AdminIdDocument {
  id: string; idType: string; verificationStatus: string;
  selfieSignedUrl: string; idSignedUrl: string;
  verifiedByUserId: string | null; verifiedAt: string | null; rejectionReason: string | null;
}
export interface AdminAttemptDetail {
  attemptId: string;
  quizId: string;
  quizTitle: string;
  candidate: {
    id: string; email: string; firstName: string; lastName: string; fatherName: string;
    mobile: string; category: string; dateOfBirth: string;
  };
  state: string;
  startedAt: string | null;
  submittedAt: string | null;
  ip: string | null;
  userAgent: string | null;
  positiveTotal: number;
  negativeTotal: number;
  penaltyTotal: number;
  finalScore: number;
  passFail: string | null;
  questions: AdminQuestionReview[];
  violations: AdminViolation[];
  snapshots: AdminSnapshot[];
  idDocuments: AdminIdDocument[];
}

// -------- Categories --------
export interface Category { id: string; name: string; questionCount: number; createdAt: string; }

// -------- Questions --------
export type Difficulty = "Easy" | "Medium" | "Hard";
export type QuestionStatus = "Draft" | "Active" | "Archived";

export interface OptionDto { id: string; text: string; isCorrect: boolean; position: number; }
export interface OptionInput { text: string; isCorrect: boolean; }

export interface QuestionListItem {
  id: string; externalId: string | null;
  categoryId: string; categoryName: string;
  text: string; difficulty: Difficulty; status: QuestionStatus;
  optionCount: number; updatedAt: string;
}
export interface QuestionListResponse {
  items: QuestionListItem[]; totalCount: number; page: number; pageSize: number;
}
export interface Question {
  id: string; externalId: string | null;
  categoryId: string; categoryName: string;
  text: string; difficulty: Difficulty;
  positiveMarks: number | null; negativeMarks: number | null;
  explanation: string | null; status: QuestionStatus;
  options: OptionDto[];
  createdAt: string; updatedAt: string;
}
export interface QuestionInput {
  externalId: string | null;
  categoryId: string;
  text: string;
  difficulty: Difficulty;
  positiveMarks: number | null;
  negativeMarks: number | null;
  explanation: string | null;
  status: QuestionStatus;
  options: OptionInput[];
}

// -------- Question import --------
export interface ImportRowPreview {
  rowNumber: number;
  outcome: "Insert" | "Update" | "Unchanged" | "Error";
  externalId: string | null;
  questionText: string | null;
  category: string | null;
  difficulty: string | null;
  existingQuestionId: string | null;
  errors: string[];
}
export interface ImportPreview {
  jobId: string;
  status: string;
  fileName: string;
  totalRows: number;
  insertCount: number;
  updateCount: number;
  unchangedCount: number;
  errorCount: number;
  rows: ImportRowPreview[];
  createdAt: string;
}
export interface ImportCommit { jobId: string; inserted: number; updated: number; unchanged: number; failed: number; }

// -------- Quizzes --------
export type QuizStatus = "Draft" | "Published" | "Archived";
export type OtpMode = "Email" | "Sms" | "Either" | "Both" | "None";
export type ShowResults = "Immediate" | "AfterEndDate" | "AdminOnly";
export type Shortfall = "Strict" | "Relaxed";

export interface DifficultyMix { easy: number; medium: number; hard: number; }
export interface ProctorSettings {
  camera: { enabled: boolean; intervalMinutes: number; intervalSeconds: number; showPreviewOnStart: boolean; allowWithoutCamera: boolean; };
  fullscreen: { enforce: boolean; penaltyPerExit: number; warningTitle: string; warningBody: string; warningButton: string; };
  tabSwitch: { detect: boolean; penaltyPerSwitch: number; maxBeforeDisqualification: number; warningTitle: string; warningBody: string; warningButton: string; };
  dualScreen: { block: boolean; onMidAttempt: string; };
  copyPaste: { disable: boolean; };
  heartbeatSeconds: number;
  clampScoreAtZero: boolean;
}
export interface PoolCategory { categoryId: string; name: string; questionCount: number; }
export interface PoolQuestion { questionId: string; text: string; difficulty: string; categoryId: string; categoryName: string; }

export interface Quiz {
  id: string;
  title: string;
  description: string;
  instructions: string;
  completionMessage: string;
  poolSize: number;
  difficultyMix: DifficultyMix;
  positiveMarksDefault: number;
  negativeMarksDefault: number;
  durationMinutes: number;
  startAt: string;
  endAt: string;
  timezoneId: string;
  randomizeQuestions: boolean;
  randomizeOptions: boolean;
  passingScore: number | null;
  attemptsAllowed: number;
  showResults: ShowResults;
  idVerificationRequired: boolean;
  proctorSettings: ProctorSettings;
  shortfallPolicy: Shortfall;
  otpMode: OtpMode;
  candidateCategories: string[];
  status: QuizStatus;
  categoryPool: PoolCategory[];
  questionPool: PoolQuestion[];
  createdAt: string;
  updatedAt: string;
}
export interface QuizListItem {
  id: string; title: string; status: QuizStatus;
  poolSize: number; durationMinutes: number;
  startAt: string; endAt: string; timezoneId: string;
  categoryCount: number; directQuestionCount: number; updatedAt: string;
}
export interface QuizListResponse { items: QuizListItem[]; totalCount: number; page: number; pageSize: number; }

export interface QuizInput {
  title: string; description: string | null; instructions: string | null;
  completionMessage: string | null;
  poolSize: number; difficultyMix: DifficultyMix;
  positiveMarksDefault: number; negativeMarksDefault: number;
  durationMinutes: number; startAt: string; endAt: string; timezoneId: string;
  randomizeQuestions: boolean; randomizeOptions: boolean;
  passingScore: number | null; attemptsAllowed: number;
  showResults: ShowResults; idVerificationRequired: boolean;
  proctorSettings: ProctorSettings | null;
  shortfallPolicy: Shortfall; otpMode: OtpMode;
  candidateCategories: string[] | null;
}

export interface PoolBucket { required: number; available: number; shortfall: number; }
export interface PoolCategoryPreview { categoryId: string; name: string; easy: number; medium: number; hard: number; total: number; }
export interface PoolPreview {
  quizId: string;
  requestedPoolSize: number;
  requestedMix: DifficultyMix;
  shortfallPolicy: string;
  easy: PoolBucket;
  medium: PoolBucket;
  hard: PoolBucket;
  totalAvailable: number;
  warnings: string[];
  byCategory: PoolCategoryPreview[];
}

export const defaultProctorSettings = (): ProctorSettings => ({
  camera: { enabled: true, intervalMinutes: 0, intervalSeconds: 35, showPreviewOnStart: true, allowWithoutCamera: false },
  fullscreen: {
    enforce: true, penaltyPerExit: 1,
    warningTitle: "You cannot minimize full screen mode!",
    warningBody: "You are not allowed to minimize full screen while taking this quiz, violation has been recorded.",
    warningButton: "Resume Quiz",
  },
  tabSwitch: {
    detect: true, penaltyPerSwitch: 1, maxBeforeDisqualification: 3,
    warningTitle: "You cannot switch tabs while taking this quiz!",
    warningBody: "You are not allowed to switch tabs, violation has been recorded.",
    warningButton: "Resume Quiz",
  },
  dualScreen: { block: true, onMidAttempt: "terminate" },
  copyPaste: { disable: true },
  heartbeatSeconds: 20,
  clampScoreAtZero: true,
});

// -------- Client --------
export const adminApi = {
  // Attempts
  listAttempts: (quizId: string, params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<AdminAttemptListResponse>(`/admin/quizzes/${quizId}/attempts`, { params }).then((r) => r.data),
  getAttempt: (attemptId: string) =>
    api.get<AdminAttemptDetail>(`/admin/attempts/${attemptId}`).then((r) => r.data),
  decideId: (attemptId: string, docId: string, decision: "Verify" | "Reject", rejectionReason?: string) =>
    api.post(`/admin/attempts/${attemptId}/id-documents/${docId}/decision`, { decision, rejectionReason }),
  bulkDelete: (quizId: string) =>
    api.post(`/admin/quizzes/${quizId}/bulk-delete-attempts`).then((r) => r.data),
  resultsExcelUrl: (quizId: string) => `${config.apiBaseUrl}/api/v1/admin/quizzes/${quizId}/results.xlsx`,
  attemptPdfUrl: (attemptId: string) => `${config.apiBaseUrl}/api/v1/admin/attempts/${attemptId}/report.pdf`,

  // Categories
  listCategories: () => api.get<Category[]>("/admin/categories").then((r) => r.data),
  createCategory: (name: string) => api.post<Category>("/admin/categories", { name }).then((r) => r.data),
  updateCategory: (id: string, name: string) => api.put(`/admin/categories/${id}`, { name }),
  deleteCategory: (id: string) => api.delete(`/admin/categories/${id}`),

  // Questions
  listQuestions: (params: { search?: string; categoryId?: string; difficulty?: Difficulty; status?: QuestionStatus; page?: number; pageSize?: number } = {}) =>
    api.get<QuestionListResponse>("/admin/questions", { params }).then((r) => r.data),
  getQuestion: (id: string) => api.get<Question>(`/admin/questions/${id}`).then((r) => r.data),
  createQuestion: (body: QuestionInput) => api.post<Question>("/admin/questions", body).then((r) => r.data),
  updateQuestion: (id: string, body: QuestionInput) => api.put<Question>(`/admin/questions/${id}`, body).then((r) => r.data),
  deleteQuestion: (id: string) => api.delete(`/admin/questions/${id}`),

  // Question import
  importTemplateUrl: () => `${config.apiBaseUrl}/api/v1/admin/questions/import/template.xlsx`,
  uploadImport: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post<ImportPreview>("/admin/questions/import", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  getImportPreview: (jobId: string) =>
    api.get<ImportPreview>(`/admin/questions/import/${jobId}`).then((r) => r.data),
  commitImport: (jobId: string) =>
    api.post<ImportCommit>(`/admin/questions/import/${jobId}/commit`).then((r) => r.data),

  // Quizzes
  listQuizzes: (params: { search?: string; status?: QuizStatus; page?: number; pageSize?: number } = {}) =>
    api.get<QuizListResponse>("/admin/quizzes", { params }).then((r) => r.data),
  getQuiz: (id: string) => api.get<Quiz>(`/admin/quizzes/${id}`).then((r) => r.data),
  createQuiz: (body: QuizInput) => api.post<Quiz>("/admin/quizzes", body).then((r) => r.data),
  updateQuiz: (id: string, body: QuizInput) => api.put<Quiz>(`/admin/quizzes/${id}`, body).then((r) => r.data),
  publishQuiz: (id: string) => api.post<Quiz>(`/admin/quizzes/${id}/publish`).then((r) => r.data),
  archiveQuiz: (id: string) => api.post(`/admin/quizzes/${id}/archive`),
  attachCategory: (quizId: string, categoryId: string) =>
    api.post(`/admin/quizzes/${quizId}/categories`, { categoryId }),
  detachCategory: (quizId: string, categoryId: string) =>
    api.delete(`/admin/quizzes/${quizId}/categories/${categoryId}`),
  attachQuestion: (quizId: string, questionId: string) =>
    api.post(`/admin/quizzes/${quizId}/questions`, { questionId }),
  detachQuestion: (quizId: string, questionId: string) =>
    api.delete(`/admin/quizzes/${quizId}/questions/${questionId}`),
  poolPreview: (quizId: string) => api.get<PoolPreview>(`/admin/quizzes/${quizId}/pool-preview`).then((r) => r.data),

  // Admin users (SuperAdmin only)
  listUsers: () => api.get<AdminUser[]>("/admin/users").then((r) => r.data),
  createUser: (body: CreateAdminUserRequest) =>
    api.post<AdminUser>("/admin/users", body).then((r) => r.data),
  resetUserPassword: (userId: string, newPassword: string) =>
    api.post(`/admin/users/${userId}/reset-password`, { newPassword }),

  // System settings
  getSettings: () => api.get<SystemSettings>("/admin/settings").then((r) => r.data),
  updateSettings: (body: SystemSettings) =>
    api.put<SystemSettings>("/admin/settings", body).then((r) => r.data),

  // Candidates
  listCandidates: (params: {
    search?: string; quizId?: string;
    registeredFrom?: string; registeredTo?: string;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<AdminCandidateListResponse>("/admin/candidates", { params }).then((r) => r.data),
  deleteCandidate: (candidateId: string, cascade: boolean) =>
    api.delete<CandidateDeleteResponse>(`/admin/candidates/${candidateId}`, { params: { cascade } })
      .then((r) => r.data),
};

export interface AdminCandidateQuizRef { quizId: string; quizTitle: string; submittedAt: string | null; state: string; }
export interface AdminCandidateListItem {
  id: string; email: string; firstName: string; lastName: string; fatherName: string;
  mobile: string; category: string; dateOfBirth: string; registeredAt: string;
  attemptCount: number;
  quizzes: AdminCandidateQuizRef[];
}
export interface AdminCandidateListResponse {
  items: AdminCandidateListItem[]; totalCount: number; page: number; pageSize: number;
}
export interface CandidateDeleteResponse {
  candidateId: string; cascadeApplied: boolean;
  deletedAttempts: number; deletedSnapshots: number; deletedIdDocuments: number;
}

export interface SystemSettings {
  cameraIntervalMinutes: number;
  cameraIntervalSeconds: number;
  cameraEnabledByDefault: boolean;
}

export type AdminRole = "SuperAdmin" | "Admin" | "Instructor";

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  roles: AdminRole[];
  createdAt: string;
}

export interface CreateAdminUserRequest {
  email: string;
  displayName: string;
  password: string;
  roles: AdminRole[];
}
